import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import TextBlock from "./TextBlock";
import { categoryColor, displayTerm } from "../graphlab/graphShared";
import { glossTitle } from "../../utils/termGlosses";
import { siteUrl } from "../../utils/constants";

/** Case-chapter block (element.lexicon-panel): the word sets as they
 *  actually are — live from the evidence API, so entry counts and terms
 *  can never go stale the way the old hand-maintained bubbles did. Set 3
 *  (transitional justice) renders full-width on top: it is the central
 *  set, whose subject — the legacies of past violations — is what the
 *  relevance classifier admits pages on; the other categories follow as
 *  cards in the site's category colors. Term chips deep-link
 *  into the Search view. */

const HRDI = "humanRightsDocumentationInitiative";
const GEOGRAPHY_CATEGORY = 4;

interface Category {
  id: number;
  label: string;
}
interface TermCount {
  term: string;
  pages: number;
}

const styles = `
.lex.container { margin-bottom: 56px; }
.lex-spine { border: 1px solid #dee2e6; border-left: 4px solid;
  border-radius: 4px; background: #fff;
  padding: 18px 22px; margin-bottom: 44px; }
.lex-head { display: flex; flex-wrap: wrap; align-items: baseline;
  gap: 8px 12px; margin-bottom: 4px; }
.lex-head .dot { width: 11px; height: 11px; border-radius: 50%;
  flex: none; align-self: center; }
.lex-head h5 { font-size: 16px; margin: 0; }
.lex-head .count { font-size: 12.5px; color: #757575; }
.lex-note { font-size: 13.5px; color: #757575; margin: 0 0 10px; }
.lex-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.lex-chips a { font-size: 13px; border: 1px solid #dee2e6;
  border-radius: 14px; padding: 3px 11px; color: #212529;
  text-decoration: none; background: #fff; }
.lex-chips a:hover { background: #f4f2ef; }
/* One full-width band per category: with complete term lists, chips
   flow horizontally instead of stacking into tall columns. */
.lex-grid { display: grid; grid-template-columns: 1fr; gap: 14px; }
.lex-card { border: 1px solid #dee2e6; border-radius: 4px;
  background: #fff; padding: 14px 16px; }
.lex-eyebrow { font-size: 12px; font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em; color: #55544f; margin: 0 0 12px;
  display: flex; align-items: center; gap: 12px; }
.lex-eyebrow::after { content: ""; flex: 1; height: 1px;
  background: #dee2e6; }
`;

interface LexiconPanelProps {
  heading?: string;
  set?: string;
}

const LexiconPanel = ({ heading, set = HRDI }: LexiconPanelProps) => {
  const { t, i18n } = useTranslation();
  const catLabel = (label: string) => t(`evidence.cat.${label}`, label);
  const api = `${siteUrl}/api/evidence`;
  const [groups, setGroups] = useState<
    { category: Category; terms: TermCount[] }[] | null>(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${api}/categories`, { params: { set } })
      .then(({ data }: { data: Category[] }) => Promise.all(
        data.map((category) =>
          axios.get(`${api}/terms`, { params: { set, category: category.id } })
            .then(({ data: terms }) => ({ category, terms })))))
      .then((all) => { if (alive) setGroups(all); })
      .catch(() => { if (alive) setGroups(null); });
    return () => { alive = false; };
  }, [api, set]);

  if (!groups) return null;

  // The central set is chosen by its number, not by its label's wording.
  const CENTRAL_SET = 3;
  const isSpine = (c: Category) => c.id === CENTRAL_SET;
  const spine = groups.find((g) => isSpine(g.category));
  const rest = groups.filter((g) => !isSpine(g.category));

  // The full word set, always — the lexicon IS the instrument, and a
  // sampled list is how the old bubbles went stale.
  const chips = (category: Category, terms: TermCount[]) => (
    <div className="lex-chips">
      {terms.map(({ term }) => (
        <a key={term} target="_blank" rel="noreferrer"
          title={glossTitle(term, i18n.language)}
          href={`/evidence?set=${encodeURIComponent(set)}`
            + `&cat=${category.id}&term=${encodeURIComponent(term)}`}>
          {displayTerm(term, category.id === GEOGRAPHY_CATEGORY)}
        </a>
      ))}
    </div>
  );

  return (
    <div className="lex container">
      <style>{styles}</style>
      {heading && <TextBlock caseTitle={heading} cssClass="mt--100" />}
      <div className="row"><div className="col-lg-12">
        <div className="inner-wrapper"><div className="inner">
          {spine && (
            <div className="lex-spine"
              style={{ borderLeftColor: categoryColor(spine.category.id) }}>
              <div className="lex-head">
                <span className="dot"
                  style={{ background: categoryColor(spine.category.id) }} />
                <h5>{catLabel(spine.category.label)}</h5>
                <span className="count">
                  {spine.terms.length} {t("evidence.findTerms", "terms")}
                  {" · "}{t("evidence.lexSpine", "the central set")}
                </span>
              </div>
              <p className="lex-note">
                {t("evidence.lexSpineNote",
                  "Pages enter the corpus through a list of anchor terms in "
                  + "sentence-shaped prose, and are counted only when a "
                  + "validated classifier judges them to concern the legacies "
                  + "of past violations — the subject of this set. Every "
                  + "other category is measured within that context.")}
              </p>
              {chips(spine.category, spine.terms)}
            </div>
          )}
          <p className="lex-eyebrow">
            {t("evidence.lexMeasured",
              "measured within the transitional justice context")}
          </p>
          <div className="lex-grid">
            {rest.map(({ category, terms }) => (
              <div className="lex-card" key={category.id}
                style={{ borderLeft:
                  `4px solid ${categoryColor(category.id)}` }}>
                <div className="lex-head">
                  <span className="dot"
                    style={{ background: categoryColor(category.id) }} />
                  <h5>{catLabel(category.label)}</h5>
                  <span className="count">{terms.length}</span>
                </div>
                {chips(category, terms)}
              </div>
            ))}
          </div>
        </div></div>
      </div></div>
    </div>
  );
};

export default LexiconPanel;
